/**
 * CodeRepositoriesPanel - Manage code repository uploads for DO-178C analysis
 * 
 * Allows users to upload C/C++ code tarballs for the AI to analyze.
 * The code is indexed and made available through AI tools like:
 * - search_code: Semantic search across code
 * - get_code_metrics: Cyclomatic complexity, nesting depth, etc.
 * - get_call_graph: Function call relationships
 * - trace_requirement_to_code: DO-178C traceability
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
  Textarea,
  useConfirm,
} from '@/components/ui';
import { logger } from '../../utils/logger';

interface CodeRepository {
  repositoryId: string;
  projectId: string;
  name: string;
  description?: string;
  filename: string;
  fileSize?: number;
  indexStatus: 'pending' | 'indexing' | 'indexed' | 'failed';
  indexError?: string;
  fileCount?: number;
  functionCount?: number;
  lineCount?: number;
  createdAt: string;
  indexedAt?: string;
}

interface CodeRepositoriesPanelProps {
  projectId: string;
  projectName: string;
}

export function CodeRepositoriesPanel({ projectId, projectName: _projectName }: CodeRepositoriesPanelProps) {
  const [repositories, setRepositories] = useState<CodeRepository[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // Upload form state
  const [repoName, setRepoName] = useState('');
  const [repoDescription, setRepoDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Polling for status updates
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  
  // Confirm dialog hook
  const confirm = useConfirm();
  
  // Load repositories
  const loadRepositories = useCallback(async () => {
    try {
      const response = await api.get(`/code-repositories?projectId=${projectId}`);
      const data = response.data as { repositories: CodeRepository[] };
      setRepositories(data.repositories || []);
      
      // Check if any are still indexing
      const hasIndexing = (data.repositories || []).some(
        (r: CodeRepository) => r.indexStatus === 'pending' || r.indexStatus === 'indexing'
      );
      
      // Start polling if there are indexing repos
      if (hasIndexing && !pollingRef.current) {
        pollingRef.current = setInterval(loadRepositories, 5000);
      } else if (!hasIndexing && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (err) {
      logger.error('Failed to load code repositories:', err);
      setError('Failed to load code repositories');
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => {
    loadRepositories();
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [loadRepositories]);
  
  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.tar.gz', '.tgz', '.zip'];
      const hasValidExt = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!hasValidExt) {
        setError('Invalid file type. Please upload a .tar.gz, .tgz, or .zip file.');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      
      // Auto-fill name from filename if empty
      if (!repoName) {
        const nameMatch = file.name.match(/^(.+?)(?:\.tar\.gz|\.tgz|\.zip)$/i);
        if (nameMatch) {
          setRepoName(nameMatch[1]);
        }
      }
    }
  };
  
  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !repoName.trim()) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      // Step 1: Create repository and get presigned URL
      const createResponse = await api.post('/code-repositories', {
        projectId,
        name: repoName.trim(),
        description: repoDescription.trim() || undefined,
        filename: selectedFile.name,
      });
      
      const { repository, uploadUrl } = createResponse.data as { 
        repository: CodeRepository; 
        uploadUrl: string;
      };
      
      setUploadProgress(10);
      
      // Step 2: Upload file to S3 using presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': 'application/gzip',
        },
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }
      
      setUploadProgress(100);
      
      // Reset form and close modal
      setRepoName('');
      setRepoDescription('');
      setSelectedFile(null);
      setShowUploadModal(false);
      
      // Add new repository to list (it will be pending)
      setRepositories(prev => [repository, ...prev]);
      
      // Start polling for status
      if (!pollingRef.current) {
        pollingRef.current = setInterval(loadRepositories, 5000);
      }
      
    } catch (err) {
      logger.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  // Handle delete
  const handleDelete = async (repositoryId: string) => {
    const confirmed = await confirm({
      title: 'Delete Code Repository',
      description: 'Are you sure you want to delete this code repository? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'destructive',
    });
    
    if (!confirmed) return;
    
    try {
      await api.delete(`/code-repositories/${repositoryId}`);
      setRepositories(prev => prev.filter(r => r.repositoryId !== repositoryId));
    } catch (err) {
      logger.error('Failed to delete repository:', err);
      setError('Failed to delete repository');
    }
  };
  
  // Handle reindex
  const handleReindex = async (repositoryId: string) => {
    try {
      await api.post(`/code-repositories/${repositoryId}/reindex`);
      loadRepositories();
    } catch (err) {
      logger.error('Failed to trigger re-index:', err);
      setError('Failed to trigger re-index');
    }
  };
  
  // Format file size
  const formatSize = (bytes?: number) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Get status badge
  const getStatusBadge = (status: CodeRepository['indexStatus'], error?: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      indexing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      indexed: 'bg-green-500/20 text-green-400 border-green-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    
    const labels: Record<string, string> = {
      pending: '⏳ Pending',
      indexing: '🔄 Indexing...',
      indexed: '✓ Indexed',
      failed: '✕ Failed',
    };
    
    return (
      <span 
        className={`px-2 py-0.5 text-xs font-medium rounded border ${styles[status]}`}
        title={error || undefined}
      >
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Code Repositories
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Upload C/C++ code for DO-178C analysis
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHelpModal(true)}
            className="text-muted-foreground"
            title="How to create a tarball"
          >
            ?
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowUploadModal(true)}
          >
            + Upload Code
          </Button>
        </div>
      </div>
      
      {error && (
        <div className="px-3 py-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded flex items-center justify-between">
          <span>{error}</span>
          <Button 
            variant="ghost"
            size="xs"
            onClick={() => setError(null)}
            className="text-destructive hover:text-destructive"
          >
            ✕
          </Button>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-5 h-5 border-2 border-muted-foreground border-t-transparent rounded-full" />
        </div>
      ) : repositories.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            No code repositories uploaded yet.
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Upload your embedded C/C++ code to enable AI-powered code analysis,
            metrics extraction, and DO-178C traceability.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUploadModal(true)}
          >
            Upload Your First Repository
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {repositories.map((repo) => (
            <div 
              key={repo.repositoryId}
              className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:border-muted-foreground/30 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground truncate">
                    {repo.name}
                  </span>
                  {getStatusBadge(repo.indexStatus, repo.indexError)}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{repo.filename}</span>
                  {repo.indexStatus === 'indexed' && (
                    <>
                      <span>•</span>
                      <span>{repo.fileCount ?? 0} files</span>
                      <span>•</span>
                      <span>{repo.functionCount ?? 0} functions</span>
                      <span>•</span>
                      <span>{(repo.lineCount ?? 0).toLocaleString()} lines</span>
                    </>
                  )}
                </div>
                {repo.description && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {repo.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                {repo.indexStatus === 'failed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReindex(repo.repositoryId)}
                    title="Retry indexing"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ↻
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(repo.repositoryId)}
                  title="Delete repository"
                  className="text-muted-foreground hover:text-destructive"
                >
                  🗑
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Code Repository</DialogTitle>
            <DialogDescription>
              Upload a compressed archive of your C/C++ source code for DO-178C analysis.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="repo-name">Repository Name *</Label>
              <Input
                id="repo-name"
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                placeholder="e.g., Navigation System v2.3"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="repo-desc">Description (optional)</Label>
              <Textarea
                id="repo-desc"
                value={repoDescription}
                onChange={(e) => setRepoDescription(e.target.value)}
                placeholder="Brief description of the codebase..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Code Archive *</Label>
              <div 
                className={`
                  border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                  transition-colors
                  ${selectedFile 
                    ? 'border-link bg-link/5' 
                    : 'border-muted-foreground/30 hover:border-muted-foreground/50'
                  }
                `}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatSize(selectedFile.size)} — Click to change
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Click to select a .tar.gz, .tgz, or .zip file
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Max size: 100 MB
                    </p>
                  </div>
                )}
              </div>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".tar.gz,.tgz,.zip,.gz,application/gzip,application/x-gzip,application/zip,application/x-tar"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            
            {uploading && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                  <div 
                    className={`bg-link h-1.5 rounded-full transition-all duration-300 ${
                      uploadProgress === 0 ? 'w-0' :
                      uploadProgress <= 10 ? 'w-[10%]' :
                      uploadProgress <= 25 ? 'w-1/4' :
                      uploadProgress <= 50 ? 'w-1/2' :
                      uploadProgress <= 75 ? 'w-3/4' :
                      uploadProgress < 100 ? 'w-[90%]' :
                      'w-full'
                    }`}
                  />
                </div>
              </div>
            )}
            
            <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
              <strong>What happens next?</strong>
              <br />
              After upload, the code will be automatically:
              <ul className="mt-1 space-y-0.5 list-disc list-inside">
                <li>Extracted and parsed for function definitions</li>
                <li>Analyzed for DO-178C metrics (complexity, nesting, etc.)</li>
                <li>Indexed for semantic search by the AI</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setRepoName('');
                setRepoDescription('');
                setSelectedFile(null);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !repoName.trim() || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Help Modal - Tarball Creation Guide */}
      <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>How to Create a Code Archive</DialogTitle>
            <DialogDescription>
              Package your source code for upload and analysis.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">Linux/macOS (Terminal)</h4>
              <pre className="bg-muted p-3 rounded text-xs overflow-x-auto font-mono">
{`cd /path/to/your/project
tar -czvf code-archive.tar.gz \\
  --exclude='.git' \\
  --exclude='build' \\
  --exclude='*.o' \\
  --exclude='*.exe' \\
  src/ include/`}
              </pre>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">Windows (PowerShell)</h4>
              <pre className="bg-muted p-3 rounded text-xs overflow-x-auto font-mono">
{`# Install 7-Zip first, then:
cd C:\\path\\to\\project
7z a -tgzip code-archive.tar.gz src\\ include\\`}
              </pre>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">What to Include</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li><strong>.c, .h files</strong> — Source and header files</li>
                <li><strong>.cpp, .hpp files</strong> — C++ source files (if applicable)</li>
                <li><strong>Configuration files</strong> — Makefile, CMakeLists.txt, etc.</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">What to Exclude</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li><strong>.git/</strong> — Version control history</li>
                <li><strong>build/, obj/</strong> — Build artifacts</li>
                <li><strong>*.o, *.a, *.so</strong> — Compiled objects</li>
                <li><strong>*.exe, *.bin</strong> — Executables</li>
                <li><strong>Third-party libraries</strong> — External dependencies</li>
              </ul>
            </div>
            
            <div className="bg-muted/30 p-3 rounded text-xs text-muted-foreground">
              <strong>💡 Tip:</strong> Only include source code you own or have rights to analyze.
              The code remains encrypted at rest and is only accessible to your project team.
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowHelpModal(false)}>
              Got It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CodeRepositoriesPanel;
